import { useEffect, useState } from "react";
import {
  APPLE_EULA_URL,
  PRIVACY_POLICY_URL,
  PRODUCT_DISPLAY_NAMES,
  PRODUCT_DURATIONS,
  nativePlatform,
  openExternalUrl,
  storeProductId,
} from "@/lib/store-billing";

/** Terms of Use shown for web/card checkout (Apple requires its own EULA). */
const WEB_TERMS_URL = "https://www.inquira.online/terms";

const AUTO_RENEW_NOTICE = {
  apple:
    "Subscription automatically renews unless canceled at least 24 hours before the end of the current subscription period. Your Apple ID account will be charged for renewal according to Apple's subscription terms.",
  google:
    "Subscription automatically renews unless canceled at least 24 hours before the end of the current subscription period. Your Google Play account will be charged for renewal according to Google Play's subscription terms.",
  web: "Subscription automatically renews at the end of each billing period until you cancel. Your saved payment method is charged automatically, and you can cancel anytime from your subscription settings.",
} as const;

interface Props {
  plan?: "monthly" | "yearly";
  /** Localized price from StoreKit / Play Billing when available. */
  price?: string | null;
  /** Fallback price copy for the web app. */
  fallbackPrice?: string;
  /** Hide the product summary (already shown by the surrounding card). */
  compact?: boolean;
  className?: string;
}

/**
 * Apple-compliant subscription disclosure (App Review 3.1.2): product name,
 * duration, price, auto-renew terms and tappable Terms of Use (Apple standard
 * EULA) + Privacy Policy links. Rendered on every paywall surface.
 */
export function StoreSubscriptionTerms({
  plan = "monthly",
  price,
  fallbackPrice = "$9.99",
  compact = false,
  className = "",
}: Props) {
  const shownPrice = price ?? fallbackPrice;
  // Resolved after mount so SSR/hydration always start from the web copy.
  const [channel, setChannel] = useState<"apple" | "google" | "web">("web");
  useEffect(() => {
    const platform = nativePlatform();
    setChannel(platform === "apple" ? "apple" : platform === "google" ? "google" : "web");
  }, []);
  const isStore = channel !== "web";
  return (
    <div className={`space-y-2 rounded-xl border border-rule bg-card/60 p-3 text-left ${className}`}>
      {!compact && (
        <div className="space-y-0.5">
          <p className="text-sm font-bold text-ink">{PRODUCT_DISPLAY_NAMES[plan]}</p>
          <p className="text-xs text-ink-muted">{PRODUCT_DURATIONS[plan]}</p>
          <p className="text-xs text-ink-muted">
            {shownPrice} {plan === "monthly" ? "per month" : "per year"}
          </p>
          {isStore && <p className="text-[10px] text-ink-muted/80">{storeProductId(plan)}</p>}
        </div>
      )}
      <p className="text-[11px] leading-snug text-ink-muted">{AUTO_RENEW_NOTICE[channel]}</p>
      <div className="flex flex-wrap gap-x-4 gap-y-1 text-[11px] font-semibold">
        <button
          type="button"
          onClick={() => openExternalUrl(channel === "apple" ? APPLE_EULA_URL : WEB_TERMS_URL)}
          className="text-neon-pink underline underline-offset-2"
        >
          {channel === "apple" ? "Terms of Use (EULA)" : "Terms of Use"}
        </button>
        <button
          type="button"
          onClick={() => openExternalUrl(PRIVACY_POLICY_URL)}
          className="text-neon-pink underline underline-offset-2"
        >
          Privacy Policy
        </button>
      </div>
    </div>
  );
}
